import { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonBase } from '@mui/material';
import ComboBox from 'components/lib/ComboBox';
import Icon, { ICONS } from 'components/lib/Icon';
import Text from 'components/lib/Text';
import { NumberTextField } from 'components/lib/TextField';
import { Designation, DesignationGroup, SelectedDesignation } from 'types';

export interface AdvancedDesignationError {
  title?: {
    message: string;
  };
  amount?: {
    message: string;
  };
}

interface AdvancedDesignationRowProps {
  allowAmountEdit?: boolean; // allowAmountEdit controls the two states of the designation row: having an editable amount/trash icon, and not
  designations: Designation[];
  designationGroups: DesignationGroup[];
  excludeDesignationIds: string[];
  designation: {
    title?: string;
    key?: string;
    id?: string;
    amount?: number;
  };
  onChange: (newData: Partial<SelectedDesignation>) => void;
  onDelete: () => void;
  onClear: () => void;
  showDelete: boolean;
  error?: AdvancedDesignationError;
}

interface AdvancedDesignationOption {
  title: string;
  id: string;
  isGroup: boolean;
}

export const AdvancedDesignationRow = ({
  allowAmountEdit = false,
  designations,
  designationGroups,
  excludeDesignationIds,
  designation,
  onChange,
  onDelete,
  onClear,
  showDelete,
  error
}: AdvancedDesignationRowProps) => {
  // Check if the current designation is in a group and pre-select that group
  const initialGroup = useMemo(() => {
    if (!designation?.id) return null;

    return (
      designationGroups.find((group) =>
        group.designations.some((d) => d.id === designation.id)
      ) || null
    );
  }, [designation?.id, designationGroups]);

  const [selectedGroup, setSelectedGroup] = useState<DesignationGroup | null>(
    initialGroup
  );
  const [inputValue, setInputValue] = useState<string>('');

  const { title: comboBoxError, amount: amountError } = error || {};

  // Update selectedGroup when designation changes
  useEffect(() => {
    setSelectedGroup(initialGroup);
  }, [initialGroup]);

  const getGroupById = useCallback(
    (id) => designationGroups.find((group) => group.id === id) ?? null,
    [designationGroups]
  );

  const options: AdvancedDesignationOption[] = useMemo(() => {
    if (selectedGroup) {
      return [
        {
          title: selectedGroup.name,
          id: '',
          isGroup: true
        }
      ].concat(
        selectedGroup.designations
          .filter((d) => !excludeDesignationIds.includes(d.id))
          .map((d) => ({
            title: d.title,
            id: d.id,
            isGroup: false
          }))
      );
    }

    return designationGroups
      .filter(
        // filter out groups that don't have any more selectable designations
        (group) =>
          group.designations.filter(
            (d) => !excludeDesignationIds.includes(d.id)
          ).length > 0
      )
      .map((group) => ({
        title: `${group.name} (${group.designations.length})`,
        id: group.id,
        isGroup: true
      }))
      .concat(
        designations
          .filter((d) => !excludeDesignationIds.includes(d.id))
          .map((d) => ({
            title: d.title,
            id: d.id,
            isGroup: false
          }))
      );
  }, [selectedGroup, designations, excludeDesignationIds, designationGroups]);

  // Convert the current designation to the option format for the ComboBox value
  const currentValue = useMemo(() => {
    if (!designation?.id || !designation?.title) {
      return null;
    }
    return {
      title: designation.title,
      id: designation.id,
      isGroup: false
    };
  }, [designation]);

  return (
    <div className="GF-AdvancedDesignationPicker__Row">
      <ComboBox
        name="designation"
        value={currentValue}
        options={options}
        getOptionLabel={(option) => option.title ?? ''}
        renderOption={(props, option, state) =>
          option.isGroup ? (
            <li key={option.id}>
              <ButtonBase
                className="GF-AdvancedDesignationPicker__GroupButton"
                onClick={() => setSelectedGroup(getGroupById(option.id))}
              >
                <Text
                  className="MuiAutocomplete-option"
                  variant="body"
                  align="left"
                  width="100%"
                  fontWeight="bold"
                >
                  {!option.id && (
                    // render left chevron if we are in a group that we can leave
                    <Icon
                      icon={ICONS.CHEVRON_LEFT}
                      style={{ marginRight: '0.5rem' }}
                    />
                  )}
                  {option.title}
                </Text>
                {option.id && (
                  // render right chevron if we can enter this group
                  <Icon
                    icon={ICONS.CHEVRON_RIGHT}
                    style={{ marginRight: '0.5rem' }}
                  />
                )}
              </ButtonBase>
            </li>
          ) : (
            <li {...props}>{option.title}</li>
          )
        }
        onChange={(_, value) => {
          if (value === null) {
            onClear();
            setInputValue('');
          } else {
            onChange(value as Partial<SelectedDesignation>);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        error={!!comboBoxError}
        helperText={comboBoxError?.message}
      />
      {allowAmountEdit && (
        <div className="GF-AdvancedDesignationPicker__Price--Container">
          <NumberTextField
            name="designation"
            defaultValue={designation?.amount ?? ''}
            onValueChange={({ value }) => {
              onChange({
                amount: Number(value)
              });
            }}
            className="GF-AdvancedDesignationPicker__Price"
            prefix="$"
            error={!!amountError}
            helperText={amountError?.message}
          />
          {showDelete && (
            <Icon
              icon={ICONS.TRASH}
              className="GF-AdvancedDesignationPicker__Delete"
              onClick={onDelete}
            />
          )}
        </div>
      )}
    </div>
  );
};
